import dayjs from 'dayjs';
import React from 'react';
import { Popover, Tooltip } from 'antd';
import { Event, EventWithTimeOverlap, EventStatus } from 'types/calendar';
import { getEventColorSetting, isCancelledEvent, isEventTimeOverlap } from 'utils/calendar';
import s from './s.module.less';
import { getEventTitleDisplay } from 'utils/calendar';
import { DEFAULT_CALENDAR_COLOR } from 'constants/common';
import { KlarityService, Prospect } from 'types/common';
import EventTitle from '../EventTitle';

type Props = {
    day: dayjs.Dayjs
    rowInx: number
    events: Event[]
    onEventEdit?: (event: Event) => void,
    onEventAdd?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void,
    timezone?: string,
    allSpecialityData?: KlarityService[],
    allClients?: Prospect[],
};

const MonthDay = (props: Props) => {
    const { day, rowInx, onEventEdit, events = [], timezone, onEventAdd, allSpecialityData } = props;

    const isCurrentDay = day.format('DD-MM-YY') === dayjs().format('DD-MM-YY');
    const eventsWithTimeOverlap: EventWithTimeOverlap[] = React.useMemo(() => {
        const ret: EventWithTimeOverlap[] = [];
        for (let i = 0; i < events.length; i++) {
            const currentEvent = events[i];
            const timeOverlapKeys: string[] = [];
            for (let j = 0; j < i; j++) {
                const event2 = events[j];
                if (isEventTimeOverlap(currentEvent, event2)) {
                    timeOverlapKeys.push(event2.id);
                }
            }
            ret.push({ ...events[i], timeOverlapKeys });
        }
        return ret;
    }, [events]);

    const displayItem = eventsWithTimeOverlap.length > 4 ? eventsWithTimeOverlap.slice(0, 4) : eventsWithTimeOverlap;
    const showMore = eventsWithTimeOverlap.length > 4;
    const moreItems = eventsWithTimeOverlap.length - 4;
    const content = showMore ?
        (

            <div className={s.moreContent}>
                <div className={s.moreT1}>{day.format('dddd, MMMM DD, YYYY')}</div>
                <div className={s.moreContent}>
                    {eventsWithTimeOverlap.map((evt, idx) => {
                        const startTimeDayjs = dayjs(evt.start.dateTime);
                        const endTimeDayjs = dayjs(evt.end.dateTime);
                        if (timezone) {
                            startTimeDayjs.tz(timezone);
                            endTimeDayjs.tz(timezone);
                        }
                        const startTime = startTimeDayjs.format(startTimeDayjs.hour() === 0 ? 'HH:mm A' : 'hh:mm A');
                        const color = evt.color || DEFAULT_CALENDAR_COLOR;

                        let timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                        if (startTimeDayjs.hour() === 0) {
                            //12 AM
                            timeLabel = `${startTimeDayjs.format('HH:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                        }
                        const toolContent = (
                            <>
                                <div className={s.toolTextTitle}>
                                    <EventTitle
                                        event={evt}
                                        allSpecialityData={allSpecialityData}
                                    />
                                </div>
                                <div className={s.tooltTextTime}>{timeLabel}</div>
                            </>
                        );
                        const isCancel = isCancelledEvent(evt);
                        return (
                            <div
                                key={idx}
                                className={`${s.moreTitle} ${isCancel ? s.cancelTitle : ''}`}
                                onClick={() => {
                                    if (onEventEdit) {
                                        onEventEdit(evt);
                                    }
                                }}
                            >
                                <Tooltip autoAdjustOverflow color="#fff" overlayClassName={s.toolText} placement="top" title={toolContent}>
                                    <div className={s.itemLabel}>
                                        <span
                                            className={s.tag}
                                            style={{ backgroundColor: color }}
                                        />
                                        <b>{startTime}</b>
                                        <EventTitle
                                            event={evt}
                                            allSpecialityData={allSpecialityData}
                                        />
                                    </div>
                                </Tooltip>

                            </div>
                        );
                    })}
                </div>
            </div>
        )
        : null;
    return (
        <div className={s.wrap}>
            <div
                className={s.item}
                onClick={() => {
                    const startTime = day.hour(dayjs().hour()).minute(30).second(0);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    let endTime = startTime.add(30, 'minute');
                    if (endTime.isAfter(dayjs(day).endOf('day'))) {
                        endTime = dayjs(day).endOf('day');
                    }
                    onEventAdd && onEventAdd(startTime, endTime);
                }}
            >
                {rowInx === 0 && (
                    <div className={s.header}>
                        {day.format('ddd').toUpperCase()}
                    </div>
                )}
                <div
                    className={s.content}
                >
                    <div><span className={isCurrentDay ? `${s.dayLabel} ${s.today}` : s.dayLabel}>{day.format('DD')}</span></div>
                    <div
                        className={s.eventWrap}
                    >
                        {displayItem.map((evt, idx) => {
                            const color = evt.color || DEFAULT_CALENDAR_COLOR;
                            const isCancel = isCancelledEvent(evt);
                            const startTimeDayjs = dayjs(evt.start.dateTime);
                            const endTimeDayjs = dayjs(evt.end.dateTime);
                            if (timezone) {
                                startTimeDayjs.tz(timezone);
                                endTimeDayjs.tz(timezone);
                            }
                            const startTime = startTimeDayjs.format(startTimeDayjs.hour() === 0 ? 'HH:mm A' : 'hh:mm A');
                            let timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            if (startTimeDayjs.hour() === 0) {
                                //12 AM
                                timeLabel = `${startTimeDayjs.format('HH:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            }
                            const toolContent = (
                                <>
                                    <div className={s.toolTextTitle}>
                                        <EventTitle
                                            event={evt}
                                            allSpecialityData={allSpecialityData}
                                        />
                                    </div>
                                    <div className={s.tooltTextTime}>{timeLabel}</div>
                                </>
                            );
                            return (
                                <div
                                    key={idx}
                                    className={`${s.title} ${isCancel ? s.cancelTitle : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onEventEdit) {
                                            onEventEdit(evt);
                                        }
                                    }}
                                >
                                    <Tooltip autoAdjustOverflow color="#fff" overlayClassName={s.toolText} placement="top" title={toolContent}>
                                        <div className={s.itemLabel}>
                                            <span
                                                className={s.tag}
                                                style={{ backgroundColor: color }}
                                            />
                                            <span className={s.time}>{startTime}
                                            </span>
                                            <EventTitle
                                                event={evt}
                                                allSpecialityData={allSpecialityData}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>
                            );
                        })}
                        {
                            showMore && (
                                <Popover content={content} trigger="click"><div className={s.moreLabel}>{moreItems} more</div></Popover>
                            )
                        }
                    </div>
                </div>
            </div>

        </div>
    );
};

export default MonthDay;
